// src/components/CrmDetailModal.jsx
import React, { useState, useEffect } from "react";

const CrmDetailModal = ({
  contact,
  mode,
  partners,
  tags,
  onSave,
  onDelete,
  onClose,
}) => {
  const [formData, setFormData] = useState({
    // Person fields
    firstName: "",
    lastName: "",
    email: "",
    mobile: "",
    phone: "",
    position: "",
    organization: "",
    anrede: "du",
    geburtsdatum: "",
    newsletter: false,
    weihnachtskarte: false,
    name: "",
    address: {
      strasse: "",
      plz: "",
      ort: "",
      land: "Schweiz",
    },
    tags: [],
    notes: "",
    type: contact?.type || "person",
  });

  const [tagInput, setTagInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [projects, setProjects] = useState([]);
  const [selectedProjects, setSelectedProjects] = useState({});
  const [projectRoles, setProjectRoles] = useState({});

  useEffect(() => {
    if (contact && mode !== "create") {
      // Finde die Organisation der Person für Adress-Fallback
      let finalAddress = { strasse: "", plz: "", ort: "", land: "Schweiz" };

      if (contact.type === "person") {
        // Prüfe ob Person eigene Adresse hat
        const hasOwnAddress =
          contact.address &&
          (contact.address.strasse ||
            contact.address.plz ||
            contact.address.ort);

        if (hasOwnAddress) {
          // Nutze eigene Adresse
          finalAddress = {
            strasse: contact.address.strasse || "",
            plz: contact.address.plz || "",
            ort: contact.address.ort || "",
            land: contact.address.land || "Schweiz",
          };
        } else if (contact.organization && partners && partners.length > 0) {
          // Nutze Organisation-Adresse
          const org = partners.find((p) => p.name === contact.organization);
          if (org && org.address) {
            finalAddress = {
              strasse: org.address.strasse || "",
              plz: org.address.plz || "",
              ort: org.address.ort || "",
              land: org.address.land || "Schweiz",
            };
          }
        }
      } else if (contact.address) {
        // Organisation - nutze eigene Adresse
        finalAddress = {
          strasse: contact.address.strasse || "",
          plz: contact.address.plz || "",
          ort: contact.address.ort || "",
          land: contact.address.land || "Schweiz",
        };
      }

      setFormData({
        ...contact,
        address: finalAddress,
        tags: contact.tags || [],
        anrede: contact.anrede || "du",
        newsletter: contact.newsletter || false,
        weihnachtskarte: contact.weihnachtskarte || false,
      });
    }
  }, [contact, mode, partners]);

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;

    if (name.startsWith("address.")) {
      const addressField = name.split(".")[1];
      setFormData((prev) => ({
        ...prev,
        address: {
          ...prev.address,
          [addressField]: value,
        },
      }));
    } else {
      setFormData((prev) => ({
        ...prev,
        [name]: type === "checkbox" ? checked : value,
      }));
    }
  };

  const handleAddTag = () => {
    if (tagInput.trim() && !formData.tags.includes(tagInput.trim())) {
      setFormData((prev) => ({
        ...prev,
        tags: [...prev.tags, tagInput.trim()],
      }));
      setTagInput("");
    }
  };

  const handleRemoveTag = (tagToRemove) => {
    setFormData((prev) => ({
      ...prev,
      tags: prev.tags.filter((tag) => tag !== tagToRemove),
    }));
  };

  useEffect(() => {
    loadProjects();
    loadProjectRelations();
  }, [contact]);

  const loadProjects = async () => {
    try {
      const ProjectsService = await import("../services/azureProjectsService");
      const allProjects = await ProjectsService.getAllProjects();
      setProjects(allProjects);
    } catch (error) {
      console.error("Fehler beim Laden der Projekte:", error);
    }
  };

  const loadProjectRelations = async () => {
    if (!contact || !contact.id) return;

    try {
      const RelationsService = await import(
        "../services/azureRelationsService"
      );
      const relations = await RelationsService.getAllRelations();

      const targetType = contact.type === "person" ? "person" : "partner";
      const relevantRelations = relations.filter(
        (r) =>
          r.targetType === targetType &&
          r.targetId === contact.id &&
          r.sourceType === "project"
      );

      const selected = {};
      const roles = {};
      relevantRelations.forEach((rel) => {
        selected[rel.sourceId] = true;
        roles[rel.sourceId] = rel.function || "";
      });

      setSelectedProjects(selected);
      setProjectRoles(roles);
    } catch (error) {
      console.error("Fehler beim Laden der Projekt-Relationen:", error);
    }
  };

  const handleProjectToggle = (projectId) => {
    setSelectedProjects((prev) => ({
      ...prev,
      [projectId]: !prev[projectId],
    }));
  };

  const handleRoleChange = (projectId, role) => {
    setProjectRoles((prev) => ({
      ...prev,
      [projectId]: role,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Validation
    if (formData.type === "person") {
      if (!formData.firstName || !formData.lastName) {
        alert("Vor- und Nachname sind erforderlich");
        return;
      }
    } else {
      if (!formData.name) {
        alert("Name ist erforderlich");
        return;
      }
    }

    setLoading(true);
    try {
      await onSave(formData, selectedProjects, projectRoles);
    } finally {
      setLoading(false);
    }
  };

  const getModalTitle = () => {
    const entityType = formData.type === "person" ? "Person" : "Organisation";
    switch (mode) {
      case "create":
        return `Neue ${entityType} erstellen`;
      case "edit":
        return `${entityType} bearbeiten`;
      default:
        return `${entityType} Details`;
    }
  };

  const isReadonly = mode === "view";

  return (
    <div className="detail-modal active">
      <div className="modal-content">
        <div className="modal-header">
          <h2 className="modal-title">{getModalTitle()}</h2>
          <button className="close-btn" onClick={onClose}>
            &times;
          </button>
        </div>

        <form onSubmit={handleSubmit}>
          {formData.type === "person" ? (
            // Person Form
            <div className="person-form">
              <div className="form-row">
                <div className="form-group">
                  <label className="form-label">Vorname *</label>
                  <input
                    type="text"
                    name="firstName"
                    className="form-input"
                    value={formData.firstName}
                    onChange={handleInputChange}
                    required
                    readOnly={isReadonly}
                  />
                </div>
                <div className="form-group">
                  <label className="form-label">Nachname *</label>
                  <input
                    type="text"
                    name="lastName"
                    className="form-input"
                    value={formData.lastName}
                    onChange={handleInputChange}
                    required
                    readOnly={isReadonly}
                  />
                </div>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label className="form-label">Telefon</label>
                  <input
                    type="tel"
                    name="phone"
                    className="form-input"
                    value={formData.phone}
                    onChange={handleInputChange}
                    readOnly={isReadonly}
                  />
                </div>
                <div className="form-group">
                  <label className="form-label">Mobile</label>
                  <input
                    type="tel"
                    name="mobile"
                    className="form-input"
                    value={formData.mobile}
                    onChange={handleInputChange}
                    readOnly={isReadonly}
                  />
                </div>
                <div className="form-group">
                  <label className="form-label">Email</label>
                  <input
                    type="email"
                    name="email"
                    className="form-input"
                    value={formData.email}
                    onChange={handleInputChange}
                    readOnly={isReadonly}
                  />
                </div>
              </div>

              <div className="form-group">
                <label className="form-label">Organisation</label>
                {isReadonly ? (
                  <input
                    type="text"
                    className="form-input"
                    value={formData.organization}
                    readOnly
                  />
                ) : (
                  <select
                    name="organization"
                    className="form-input"
                    value={formData.organization}
                    onChange={handleInputChange}
                  >
                    <option value="">Keine Organisation</option>
                    {partners
                      .slice()
                      .sort((a, b) => a.name.localeCompare(b.name))
                      .map((partner) => (
                        <option key={partner.id} value={partner.name}>
                          {partner.name}
                        </option>
                      ))}
                  </select>
                )}
              </div>
              <div className="form-group">
                <label className="form-label">Position</label>
                <input
                  type="text"
                  name="position"
                  className="form-input"
                  value={formData.position}
                  onChange={handleInputChange}
                  readOnly={isReadonly}
                  placeholder="z.B. PL, Partnerin, Hausmeister"
                />
              </div>

              <div className="form-group">
                <label className="form-label">Anrede</label>
                <div className="anrede-selector">
                  <div className="anrede-option">
                    <input
                      type="radio"
                      id="du"
                      name="anrede"
                      value="du"
                      checked={formData.anrede === "du"}
                      onChange={handleInputChange}
                      disabled={isReadonly}
                    />
                    <label htmlFor="du">Du</label>
                  </div>
                  <div className="anrede-option">
                    <input
                      type="radio"
                      id="sie"
                      name="anrede"
                      value="sie"
                      checked={formData.anrede === "sie"}
                      onChange={handleInputChange}
                      disabled={isReadonly}
                    />
                    <label htmlFor="sie">Sie</label>
                  </div>
                </div>
              </div>

              <div className="form-group">
                <label className="form-label">Geburtsdatum</label>
                <input
                  type="date"
                  name="geburtsdatum"
                  className="form-input"
                  value={formData.geburtsdatum}
                  onChange={handleInputChange}
                  readOnly={isReadonly}
                />
              </div>

              <div className="form-group">
                <label className="form-label">
                  <input
                    type="checkbox"
                    name="newsletter"
                    className="form-checkbox"
                    checked={formData.newsletter}
                    onChange={handleInputChange}
                    disabled={isReadonly}
                  />
                  Newsletter erhalten
                </label>
              </div>

              <div className="form-group">
                <label className="form-label">
                  <input
                    type="checkbox"
                    name="weihnachtskarte"
                    className="form-checkbox"
                    checked={formData.weihnachtskarte}
                    onChange={handleInputChange}
                    disabled={isReadonly}
                  />
                  Weihnachtskarte senden
                </label>
              </div>
            </div>
          ) : (
            // Organization Form
            <div className="organization-form">
              <div className="form-group">
                <label className="form-label">Name *</label>
                <input
                  type="text"
                  name="name"
                  className="form-input"
                  value={formData.name}
                  onChange={handleInputChange}
                  required
                  readOnly={isReadonly}
                />
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label className="form-label">Telefon</label>
                  <input
                    type="tel"
                    name="phone"
                    className="form-input"
                    value={formData.phone}
                    onChange={handleInputChange}
                    readOnly={isReadonly}
                  />
                </div>
                <div className="form-group">
                  <label className="form-label">Email</label>
                  <input
                    type="email"
                    name="email"
                    className="form-input"
                    value={formData.email}
                    onChange={handleInputChange}
                    readOnly={isReadonly}
                  />
                </div>
                <div className="form-group">
                  <label className="form-label">Website</label>
                  <input
                    type="url"
                    name="website"
                    className="form-input"
                    value={formData.website || ""}
                    onChange={handleInputChange}
                    readOnly={isReadonly}
                    placeholder="https://..."
                  />
                </div>
              </div>
            </div>
          )}

          {/* Address Section - Common for both */}
          <div className="address-group">
            <div className="address-title">Adresse</div>
            <div className="form-group">
              <label className="form-label">Strasse</label>
              <input
                type="text"
                name="address.strasse"
                className="form-input"
                value={formData.address.strasse}
                onChange={handleInputChange}
                readOnly={isReadonly}
              />
            </div>
            <div className="form-row">
              <div className="form-group">
                <label className="form-label">PLZ</label>
                <input
                  type="text"
                  name="address.plz"
                  className="form-input"
                  value={formData.address.plz}
                  onChange={handleInputChange}
                  readOnly={isReadonly}
                />
              </div>
              <div className="form-group">
                <label className="form-label">Ort</label>
                <input
                  type="text"
                  name="address.ort"
                  className="form-input"
                  value={formData.address.ort}
                  onChange={handleInputChange}
                  readOnly={isReadonly}
                />
              </div>
            </div>
            <div className="form-group">
              <label className="form-label">Land</label>
              <input
                type="text"
                name="address.land"
                className="form-input"
                value={formData.address.land}
                onChange={handleInputChange}
                readOnly={isReadonly}
              />
            </div>
          </div>

          {/* Tags Section */}
          <div className="form-group">
            <label className="form-label">Tags</label>
            <div className="tags-section">
              <div className="tags">
                {formData.tags.map((tag, index) => (
                  <span key={index} className="tag">
                    {tag}
                    {!isReadonly && (
                      <button
                        type="button"
                        className="tag-remove"
                        onClick={() => handleRemoveTag(tag)}
                      >
                        &times;
                      </button>
                    )}
                  </span>
                ))}
              </div>
              {!isReadonly && (
                <div className="tag-input-group">
                  <input
                    type="text"
                    value={tagInput}
                    onChange={(e) => setTagInput(e.target.value)}
                    placeholder="Tag hinzufügen..."
                    className="form-input"
                    onKeyPress={(e) =>
                      e.key === "Enter" && (e.preventDefault(), handleAddTag())
                    }
                  />
                  <button
                    type="button"
                    onClick={handleAddTag}
                    className="btn btn-secondary"
                  >
                    Hinzufügen
                  </button>
                </div>
              )}
            </div>
          </div>

          {/* Notes Section */}
          <div className="form-group">
            <label className="form-label">Notizen</label>
            <textarea
              name="notes"
              className="form-textarea"
              value={formData.notes}
              onChange={handleInputChange}
              readOnly={isReadonly}
              rows="4"
            />
          </div>

          {/* Projekt-Zuordnung */}
          <div className="projects-section">
            <h3>Projekte</h3>
            <div className="projects-table">
              <table>
                <thead>
                  <tr>
                    <th style={{ width: "40px" }}></th>
                    <th>Code</th>
                    <th>Name</th>
                    <th>Rolle</th>
                  </tr>
                </thead>
                <tbody>
                  {projects.map((project) => (
                    <tr key={project.id}>
                      <td>
                        <input
                          type="checkbox"
                          checked={selectedProjects[project.id] || false}
                          onChange={() => handleProjectToggle(project.id)}
                          disabled={isReadonly}
                        />
                      </td>
                      <td>{project.code}</td>
                      <td>{project.title}</td>
                      <td>
                        <input
                          type="text"
                          value={projectRoles[project.id] || ""}
                          onChange={(e) =>
                            handleRoleChange(project.id, e.target.value)
                          }
                          disabled={!selectedProjects[project.id] || isReadonly}
                          placeholder="Rolle eingeben"
                          className="role-input"
                        />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {/* Action Buttons */}
          <div className="modal-actions">
            {mode === "view" && (
              <>
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={() => window.location.reload()} // Trigger edit mode
                >
                  Bearbeiten
                </button>
                <button
                  type="button"
                  className="btn btn-danger"
                  onClick={() => onDelete(contact)}
                >
                  Löschen
                </button>
              </>
            )}

            {(mode === "edit" || mode === "create") && (
              <>
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={onClose}
                >
                  Abbrechen
                </button>
                <button
                  type="submit"
                  className="btn btn-primary"
                  disabled={loading}
                >
                  {loading ? "Speichere..." : "Speichern"}
                </button>
              </>
            )}

            {mode === "view" && (
              <button
                type="button"
                className="btn btn-secondary"
                onClick={onClose}
              >
                Schliessen
              </button>
            )}
          </div>
        </form>
      </div>
    </div>
  );
};

export default CrmDetailModal;
